import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";

interface ReassignRiskModalProps {
  open: boolean;
  onClose: () => void;
}

const ReassignRiskModal: React.FC<ReassignRiskModalProps> = ({
  open,
  onClose,
}) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const [division, setDivision] = useState<string>("");
  const [department, setDepartment] = useState<string>("");
  const [assignee, setAssignee] = useState<string>("");
  const [departments, setDepartments] = useState<
    { id: string; name: string }[]
  >([]);
  const [assignees, setAssignees] = useState<{ id: string; name: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record) {
      setDivision(record.division || "");
      setDepartment(record.department || "");
      setAssignee(record.assignee || "");
    }
  }, [record, open]);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!division) {
        setDepartments([]);
        return;
      }
      const filter: any = {};
      if (division) {
        filter.division = division;
      }

      try {
        const response = await (dataProvider as any).getUnique(
          "rmf/employees",
          {
            field: "department",
            filter,
          }
        );
        setDepartments(
          response.map((value: string) => ({
            id: value,
            name: value,
          }))
        );
      } catch (error) {
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, [division, dataProvider]);

  useEffect(() => {
    const fetchAssignees = async () => {
      if (!division && !department) {
        setAssignees([]);
        return;
      }
      const filter: any = {};
      if (division) {
        filter.division = division;
      }
      if (department) {
        filter.department = department;
      }

      try {
        const response = await (dataProvider as any).getUnique(
          "rmf/employees",
          {
            field: "email",
            filter,
          }
        );
        const mapped = response.map((value: string) => ({
          id: value,
          name: value,
        }));
        setAssignees(mapped);

        // If current assignee is no longer valid for the selected department,
        // clear the selection.
        if (assignee && !response.includes(assignee)) {
          setAssignee("");
        }
      } catch (error) {
        setAssignees([]);
      }
    };

    fetchAssignees();
  }, [division, department, assignee, dataProvider]);

  const handleSubmit = async () => {
    if (!record?.id) {
      notify("Risk ID not found", { type: "error" });
      return;
    }

    if (!department || !assignee) {
      notify("Department and Assignee are required", { type: "warning" });
      return;
    }

    setLoading(true);
    try {
      await (dataProvider as any).custom(
        `rmf/risks/${record.id}/reassign`,
        "PUT",
        {
          assignee,
        }
      );
      notify("Risk reassigned successfully", { type: "success" });
      refresh();
      onClose();
    } catch (error: any) {
      notify(error.message || "Failed to reassign risk", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Reassign Risk
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth disabled={loading}>
            <InputLabel id="department-label">Department</InputLabel>
            <Select
              labelId="department-label"
              value={department}
              label="Department"
              onChange={(e) => setDepartment(e.target.value as string)}
            >
              {departments.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={loading}>
            <InputLabel id="assignee-label">Assignee</InputLabel>
            <Select
              labelId="assignee-label"
              value={assignee}
              label="Assignee"
              onChange={(e) => setAssignee(e.target.value as string)}
            >
              {assignees.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !department || !assignee}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignRiskModal;
